import type { ComponentType } from 'react';
import { cn } from '../../lib/utils';
import { Field, FieldContent, FieldError, FieldLabel } from '../../ui/field';
import { fieldRegistry } from '../registry';
import type { BaseFieldProps, FormFieldSchema } from '../types';

export interface FieldRendererProps {
  field: FormFieldSchema;
  mode: 'view' | 'edit';
  value: unknown;
  onChange?: (value: unknown) => void;
  onUploadFile?: (file: File, category?: string) => Promise<number>;
  onGetFile?: (fileId: number) => Promise<{ fileName: string; fileType: string; viewUrl?: string }>;
  onRenameFile?: (fileId: number, newName: string) => Promise<void>;
  error?: string;
  className?: string;
}

export function FieldRenderer({
  field,
  mode,
  value,
  onChange,
  onUploadFile,
  onGetFile,
  onRenameFile,
  error,
  className,
}: FieldRendererProps) {
  const isView = mode === 'view';
  const definition = fieldRegistry.get(field.type);

  if (!definition) {
    return (
      <div className="text-destructive text-sm font-medium">Unknown field type: {field.type}</div>
    );
  }

  const commonProps = {
    field,
    mode,
    value,
    onChange,
    onUploadFile,
    onGetFile,
    onRenameFile,
    error,
    className,
  };

  const FieldComponent = (
    isView ? definition.viewComponent : definition.editComponent
  ) as ComponentType<BaseFieldProps<FormFieldSchema, unknown>>;

  // If the field is presentational, we render it directly without the label/error wrapper
  if (definition.isPresentational || field.isPresentational) {
    return <FieldComponent {...commonProps} />;
  }

  // Standard fields are wrapped in the layout container
  const showLabel = field.type !== 'checkbox' || isView;

  return (
    <Field className={cn('py-2', className)} orientation="vertical" data-invalid={!!error}>
      {showLabel && (
        <FieldLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
          {field.label}
          {field.required && <span className="text-destructive ml-1">*</span>}
        </FieldLabel>
      )}
      <FieldContent>
        <FieldComponent {...commonProps} />
      </FieldContent>
      {!isView && error && <FieldError>{error}</FieldError>}
    </Field>
  );
}
